import { useEffect, useMemo, useRef, useState } from 'react'
import { useRecoilValue } from 'recoil'
import {
    ContentSearchParams,
    ContentView,
    SpamSearchResult,
    useCreateReadReceiptsMutation,
    useDeleteReadReceiptsMutation
} from '../../api/client'
import { useAsyncContentSearch } from '../../hooks/useAsyncContentSearch'
import { conversationViewEnabledState } from '../../store/DomainStore'
import { toastError, toastSuccess } from '../Notification'
import Pagination from '../Pagination'
import NoResults from './NoResults'
import QueryFailed from './QueryFailed'
import SearchStatusIndicator from './SearchStatusIndicator'
import SelectedContentBar from './SelectedContentBar'
import ContentCard from './content-card/ContentCard'
import ConversationDrawer from '../conversation/ConversationDrawer'
import SearchParameters, { mapToContentListSearchParams } from './search/searchParameters'
import { useContentCardPlumbing } from './useContentCardPlumbing'

type Props = {
    searchParameters: SearchParameters
    showOwningAccount: boolean
    enableBulkActions: boolean
    scores?: SpamSearchResult[]
    hidePagination?: boolean
    // Callbacks in case the parent wants these values. Optional.
}

// Page size and first page (zero index)
export const DEFAULT_PAGINATION_LIMIT = 20
export const DEFAULT_PAGINATION_OFFSET = 0

function ContentList(props: Props) {
    const { searchParameters, showOwningAccount, enableBulkActions, scores, hidePagination } = props
    const conversationViewEnabled = useRecoilValue(conversationViewEnabledState)

    // Establish offset and limit
    const [offset, setOffset] = useState<number>(DEFAULT_PAGINATION_OFFSET)
    const [limit, setLimit] = useState(DEFAULT_PAGINATION_LIMIT)

    const [selectedContent, setSelectedContent] = useState<Map<number, ContentView>>(
        new Map<number, ContentView>()
    )

    const selectedSocialMediaServiceIds = useMemo(
        () => searchParameters.selectedSocialMediaServices.map((service) => service.id),
        [searchParameters.selectedSocialMediaServices]
    )

    // Convert to a map to make lookup faster
    const scoresMap = new Map<number, number>()
    if (scores) {
        scores.forEach((score) => {
            if (score.contentId !== undefined && score.similarityScore !== undefined) {
                //@ts-ignore - compile throwing a wobbly saying contentId might be undefined.
                scoresMap.set(score.contentId, score.similarityScore)
            }
        })
    }

    /** ******************************************************
    SEARCH CONTENT - Using async search to avoid timeout issues
    */
    // The mapping is shared with the post page's Threaded tab, so both tabs send the
    // server exactly the same filters. The dependency list stays field by field, as it
    // was when the mapping lived inline, so the memo only recomputes when a filter does.
    const searchParams: ContentSearchParams = useMemo(
        () =>
            mapToContentListSearchParams(searchParameters, {
                maxResults: limit,
                skipResults: offset
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            searchParameters.timeDuration,
            searchParameters.selectedAccountIds,
            searchParameters.authorProfileIds,
            searchParameters.selectedTeams,
            searchParameters.moderation,
            searchParameters.startDateTime,
            searchParameters.endDateTime,
            selectedSocialMediaServiceIds,
            searchParameters.keyword,
            searchParameters.selectedClassifiers,
            searchParameters.adOrOrganic,
            searchParameters.contentIds,
            searchParameters.postIds,
            searchParameters.owned,
            searchParameters.readReceipt,
            searchParameters.excludeOwnComments,
            searchParameters.dmFilter,
            searchParameters.hasMedia,
            limit,
            offset
        ]
    )

    // Drives the displayed results.
    const asyncSearch = useAsyncContentSearch(searchParams)

    // Stable serialized key for searchParams — used as an effect dependency
    // to trigger searches when params change (including pagination).
    const searchParamsKey = useMemo(() => JSON.stringify(searchParams), [searchParams])

    // Track previous search params to detect changes (for clearing results)
    const prevSearchParamsRef = useRef<string | null>(null)

    // Clear results when filter params change (not pagination)
    useEffect(() => {
        const filterParams = JSON.stringify({
            timeDuration: searchParameters.timeDuration?.toISOString(),
            accountIds: searchParameters.selectedAccountIds,
            authorProfileIds: searchParameters.authorProfileIds,
            teamIds: searchParameters.selectedTeams,
            moderation: searchParameters.moderation,
            startDateTime: searchParameters.startDateTime?.toISOString(),
            endDateTime: searchParameters.endDateTime?.toISOString(),
            socialMediaServiceIds: selectedSocialMediaServiceIds,
            keyword: searchParameters.keyword,
            selectedClassifiers: searchParameters.selectedClassifiers,
            adOrOrganic: searchParameters.adOrOrganic,
            contentIds: searchParameters.contentIds,
            postIds: searchParameters.postIds,
            owned: searchParameters.owned,
            readReceipt: searchParameters.readReceipt,
            excludeOwnComments: searchParameters.excludeOwnComments,
            dmFilter: searchParameters.dmFilter,
            hasMedia: searchParameters.hasMedia
        })

        if (prevSearchParamsRef.current !== null && prevSearchParamsRef.current !== filterParams) {
            // Filter params changed - clear results so skeleton shows
            asyncSearch.clearResults()
        }
        prevSearchParamsRef.current = filterParams
        // Only clearResults is used from asyncSearch - using the full object causes unnecessary effect
        // executions during polling since the object reference changes on every state update
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        searchParameters.timeDuration,
        searchParameters.selectedAccountIds,
        searchParameters.authorProfileIds,
        searchParameters.selectedTeams,
        searchParameters.moderation,
        searchParameters.startDateTime,
        searchParameters.endDateTime,
        selectedSocialMediaServiceIds,
        searchParameters.keyword,
        searchParameters.selectedClassifiers,
        searchParameters.adOrOrganic,
        searchParameters.contentIds,
        searchParameters.postIds,
        searchParameters.owned,
        searchParameters.readReceipt,
        searchParameters.excludeOwnComments,
        searchParameters.dmFilter,
        searchParameters.hasMedia,
        asyncSearch.clearResults
    ])

    // Trigger search when params change (including pagination)
    useEffect(() => {
        asyncSearch.executeSearch()
        // We only want to run this when searchParams changes.
        // searchParamsKey is a stable memoized string derived from searchParams.
        // executeSearch is intentionally omitted — it changes identity when params
        // change (since params is in its dependency array), which would cause a
        // double-fire. The key already captures param changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParamsKey])

    // Derive content and count from async search results
    // Memoised so its identity changes only when the results do: the card plumbing
    // reseeds its action cache on every new `contents` identity.
    const contents = useMemo(
        () => (asyncSearch.results || []) as ContentView[],
        [asyncSearch.results]
    )
    const total = asyncSearch.totalCount || 0
    const countLoading = asyncSearch.totalCount === null
    const error = asyncSearch.error ? { message: asyncSearch.error } : null

    // Pass the account ID if we're viewing content from a single account to get shadow ban status
    const singleAccountId =
        searchParameters.selectedAccountIds.length === 1
            ? searchParameters.selectedAccountIds[0]
            : undefined

    // Profiles, enrichment, translation, per-card read receipts and the action cache
    // for the cards below. Shared with the other surfaces that render ContentCards.
    const plumbing = useContentCardPlumbing(contents, {
        singleAccountId,
        onReadReceiptChange: asyncSearch.executeSearch
    })

    /** ******************************************************
        READ RECEIPT
        */

    /*
    We want to make sure that the loading state of the read/unread bulk buttons is correct.
    The process of marking all as read/unread involves two steps:
    1. creating the read receipts - represented by the mutation's loading property
    2. updating the contents so that the correct read/unread status is displayed - represented by the search loading and is true whenever content loading or refetch occurs.

    When the bulk action is initiated the first goes true, then false as the second goes true then false.

    To ensure that the bulk icon correctly shows a loading indicator for the duration of the above two loading states we need
    to introduce a third state that represntes both. This is a bit more work than just relying on the Apollo supplied loading state
    but lets us be really specific in what we display.
    */

    const [markAsReadCreationAndRefetchLoading, setMarkAsReadCreationAndRefetchLoading] =
        useState(false)
    const [markAsUnreadCreationAndRefetchLoading, setMarkAsUnreadCreationAndRefetchLoading] =
        useState(false)

    const [createReadReceiptsMutation] = useCreateReadReceiptsMutation({
        onError: (error) => {
            setMarkAsReadCreationAndRefetchLoading(false)
            toastError(error.message)
        },
        onCompleted: async () => {
            await asyncSearch.executeSearch()
            setMarkAsReadCreationAndRefetchLoading(false)
            toastSuccess(`Marked selected content as read`)
        }
    })

    const [deleteReadReceiptsMutation] = useDeleteReadReceiptsMutation({
        onError: (error) => {
            setMarkAsUnreadCreationAndRefetchLoading(false)
            toastError(error.message)
        },
        onCompleted: async () => {
            await asyncSearch.executeSearch()
            setMarkAsUnreadCreationAndRefetchLoading(false)
            toastSuccess(`Marked selected content as unread`)
        }
    })

    const handleReadAll = async () => {
        setMarkAsReadCreationAndRefetchLoading(true)
        await createReadReceiptsMutation({
            variables: {
                contentIds: Array.from(selectedContent.values()).map((content) => content.id)
            }
        })
    }
    const handleUnreadAll = async () => {
        setMarkAsUnreadCreationAndRefetchLoading(true)
        await deleteReadReceiptsMutation({
            variables: {
                contentIds: Array.from(selectedContent.values()).map((content) => content.id)
            }
        })
    }

    /** ******************************************************
     * HIDE ALL / BULK HIDE
     *
     * We want to make sure that the loading indicator for the hide all bulk action stays visible
     * when, following the actual hide, we refetch the content and hence the content actions. Doing
     * this ensures that a) the hide is selected on the content action list and b) we have an up to
     * date copy of the content list.
     *
     * To ensure we can do this in a controlled way we introduce a couple more loading states and
     * manage them here and in the next function
     */
    const [hideAllRefetchLoading, setHideAllRefetchLoading] = useState(false)
    const [unhideAllRefetchLoading, setUnhideAllRefetchLoading] = useState(false)

    const handleHideAll = async function () {
        setHideAllRefetchLoading(true)
        await asyncSearch.executeSearch()
        setHideAllRefetchLoading(false)
    }

    const handleUnhideAll = async function () {
        setUnhideAllRefetchLoading(true)
        await asyncSearch.executeSearch()
        setUnhideAllRefetchLoading(false)
    }

    /** ******************************************************
     */
    // Reset offset to 0 when filter parameters change
    useEffect(() => {
        setOffset(0)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        searchParameters.keyword,
        searchParameters.moderation,
        searchParameters.startDateTime,
        searchParameters.endDateTime,
        searchParameters.selectedClassifiers,
        searchParameters.account
    ])

    const handleSelectionChange = (content: ContentView, isSelected: boolean) => {
        if (isSelected) {
            setSelectedContent(new Map([...selectedContent, [content.id, content]]))
        } else {
            const newSelectedContent = new Map([...selectedContent])
            newSelectedContent.delete(content.id)
            setSelectedContent(newSelectedContent)
        }
    }

    /* Show no results message if all queries have returned nothing and loading has finished
     */
    const showNoResults =
        !error && // There are no errors
        !asyncSearch.isSearching && // Search is not in progress
        (asyncSearch.results?.length || 0) === 0 && // There are no results
        total === 0 // Prevent unmount during pagination (clearContent empties results but preserves totalCount)

    if (showNoResults) {
        // Show the no results display if all queries have returned nothing and loading has finished
        return (
            <div className={`stretchy`}>
                <NoResults />
            </div>
        )
    }

    // There are some results
    const showPagination = !showNoResults && !hidePagination
    const showControls = !showNoResults && enableBulkActions

    function handlePaginationChange(newOffset: number, newLimit: number) {
        /**
         * FIXME:
         * See https://arwen-ai.atlassian.net/browse/AA-1071
         *
         * We've discussed letting the user mark all content as read when they change the page.
         * I (DC, 2025-01) tried implementing this and created the ConfirmMarkAsReadModal modal to do so.
         *
         * However in practice this was super annoying. I think we need a new approach that needs further discussion.
         *
         * I'm drawing this work to a close for now to ensure we can deliver the essentials. My suggestion is that we
         * add some new ">"" and "<" buttons to the Paginator that explicitly mark all content as read when clicked.
         * */
        // Clear content so the loading indicator shows for the new page,
        // but preserve totalCount so the pagination stays stable
        asyncSearch.clearContent()
        // Store the offset and limit so we can action the result of the confirmation dialog.
        let changedOffset = newOffset < 0 ? 0 : newOffset
        setOffset(changedOffset)
        setLimit(newLimit)
    }

    return (
        <div className={`stretchy`}>
            {error ? (
                <QueryFailed errorMessage={error?.message} />
            ) : (
                <>
                    {showPagination && (
                        <div className="p-3 bg-white z-2">
                            <Pagination
                                limit={limit}
                                offset={offset}
                                total={total}
                                onChange={handlePaginationChange}
                                loading={countLoading}
                            />
                        </div>
                    )}
                    {showControls && (
                        <SelectedContentBar
                            selectedContent={selectedContent}
                            onClearSelectedContent={() =>
                                setSelectedContent(new Map<number, ContentView>())
                            }
                            onActionClick={plumbing.onContentAction}
                            onSelectAll={() => {
                                const newSelectedContent = new Map<number, ContentView>()
                                contents.forEach((content) => {
                                    newSelectedContent.set(content.id, content)
                                })
                                setSelectedContent(
                                    new Map<number, ContentView>([
                                        ...Array.from(selectedContent.entries()),
                                        ...Array.from(newSelectedContent.entries())
                                    ])
                                )
                            }}
                            onReadAll={handleReadAll}
                            onUnreadAll={handleUnreadAll}
                            showMarkAsReadLoadingIndicator={markAsReadCreationAndRefetchLoading}
                            showMarkAsUnreadLoadingIndicator={markAsUnreadCreationAndRefetchLoading}
                            onHideAll={handleHideAll}
                            onUnhideAll={handleUnhideAll}
                            showHideAllLoadingIndicator={hideAllRefetchLoading}
                            showUnhideAllLoadingIndicator={unhideAllRefetchLoading}
                            actionCacheMap={plumbing.actionCacheMap}
                        ></SelectedContentBar>
                    )}
                    {/* Show status indicator only for initial searches (no previous results) */}
                    {asyncSearch.isSearching && !asyncSearch.hasPreviousResults && (
                        <SearchStatusIndicator
                            status={asyncSearch.status}
                            message={asyncSearch.message}
                            elapsedMs={asyncSearch.elapsedMs}
                            isSlowSearch={asyncSearch.isSlowSearch}
                        />
                    )}
                    {/* Show content when not searching, or during a refetch with previous results */}
                    {(!asyncSearch.isSearching || asyncSearch.hasPreviousResults) &&
                        contents.map((content) => {
                            // This item is selected if it exists in the map.
                            const isSelected = selectedContent.has(content.id)

                            return (
                                <div
                                    className="flex flex-col items-stretch hover:bg-gray-50 border-t border-solid border-gray-300 animate-fadeIn"
                                    key={content.id}
                                >
                                    <ContentCard
                                        key={content.id}
                                        content={content}
                                        showAccount={showOwningAccount}
                                        onContentAction={plumbing.onContentAction}
                                        enriching={plumbing.enriching}
                                        detectedLanguage={
                                            plumbing.detectedLanguageMap.get(content.content) ??
                                            null
                                        }
                                        translatedText={plumbing.translationCache.get(
                                            content.content
                                        )}
                                        translating={plumbing.translatingTexts.has(content.content)}
                                        onTranslateClick={() =>
                                            plumbing.onTranslateItem(content.content)
                                        }
                                        profileCache={plumbing.profileCache}
                                        enrichedContentMap={plumbing.enrichedContentMap}
                                        actionCacheMap={plumbing.actionCacheMap}
                                        isSelected={isSelected}
                                        onSelectionChange={handleSelectionChange}
                                        enableBulkActions={enableBulkActions}
                                        score={scoresMap.get(content.id)}
                                        onMarkAsReadClick={plumbing.onMarkAsReadClick}
                                        onMarkAsUnreadClick={plumbing.onMarkAsUnreadClick}
                                        onShadowBanChange={plumbing.onShadowBanChange}
                                        newProfileIds={plumbing.newProfileIds}
                                    />
                                </div>
                            )
                        })}
                    {/* Not mounted at all without the rollout toggle, so a stale
                        open id can never fetch a thread the API would refuse. */}
                    {conversationViewEnabled && (
                        <ConversationDrawer
                            onContentAction={plumbing.onContentAction}
                            profileCache={plumbing.profileCache}
                            actionCacheMap={plumbing.actionCacheMap}
                            onMarkAsReadClick={plumbing.onMarkAsReadClick}
                            onMarkAsUnreadClick={plumbing.onMarkAsUnreadClick}
                        />
                    )}
                    {showPagination && (
                        <div className="m-3">
                            <Pagination
                                limit={limit}
                                offset={offset}
                                total={total}
                                onChange={handlePaginationChange}
                                loading={countLoading}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default ContentList
